import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import PageHeader from '@uikit/old/PageHeader/PageHeader';
import HeaderActions from '@uikit/old/HeaderTemplate/HeaderActions/HeaderActions';
import WebsitesTable from '@components/Publisher/Websites/WebsitesTable/WebsitesTable';
import ImportWebsitesModal from '@components/Publisher/Websites/ImportWebsitesModal/ImportWebsitesModal';
import {
  downloadImportReport,
  getImportSummary,
} from '@components/Publisher/Websites/ImportWebsitesModal/utils';

import usePublisherWebsiteImport from '@hooks/usePublisherWebsiteImport';
import { FilterJoins, FilterOperationsMapForString } from '@constants/filters/filterOperations';
import { getPublisherWebsiteFilterFields } from '@constants/filters/publisherWebsiteFilterFields';

import { addNotification } from '@redux/actions/notifications.actions';
import { getCurrentWorkspaceId } from '@redux/selectors/workspaces.selectors';
import { redirectUserTo } from '@utils/historyHandler';
import { isFeatureEnabled } from '@utils/featureFlags';
import { PublisherWebsiteFilterType, PublisherWebsiteImportType } from '@ts/publishers.types';
import { FilterFieldType } from '@ts/filterField.type';
import debounce from '@utils/debounce';
import urls from '@constants/urls';

import {
  PublisherWebsiteFilterField,
  PublisherWebsiteFilterFieldMap,
} from 'respona_api/generated/order-publisher_pb';

import './Websites.css';

const initialFilterState: Record<string, FilterFieldType<PublisherWebsiteFilterFieldMap>> = {
  '1': {
    order: 0,
    field: PublisherWebsiteFilterField.STRING_WEBSITE_DOMAIN,
    value: '',
    operation: FilterOperationsMapForString[2].value,
    join: FilterJoins[0].value,
  },
};

function Websites(): JSX.Element {
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const history = useHistory();
  const dispatch = useDispatch();
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [appliedFilters, setAppliedFilters] = useState<PublisherWebsiteFilterType[]>([]);

  // The modal never shows a finished import again, so this notification is where the result
  // and its report live; it stays until dismissed.
  const handleImportFinished = (item: PublisherWebsiteImportType) =>
    dispatch(
      addNotification({
        title: `Import of ${item.fileName || 'websites'} finished: ${getImportSummary(item)}`,
        type: item.failedRows > 0 ? 'warning' : 'success',
        onConfirmText: 'Download report',
        onConfirm: () =>
          downloadImportReport(item.id, workspaceId).catch(() =>
            dispatch(
              addNotification({ title: 'The report could not be downloaded', type: 'error' })
            )
          ),
      })
    );

  const websiteImport = usePublisherWebsiteImport(handleImportFinished);

  function createNew() {
    redirectUserTo(history, urls.WEBSITE_STEP_ONE(workspaceId, ''));
  }

  const debouncedSetSearchString = useCallback(
    debounce((value) => {
      setAppliedFilters((prevAppliedFilters) => {
        const [_, ...rest] = prevAppliedFilters;

        return [
          {
            ...initialFilterState['1'],
            value,
          },
          ...rest,
        ];
      });
    }, 500),
    []
  );

  return (
    <div className="page-table" data-testid="publisher-websites-page">
      <div className="page-table__content">
        <PageHeader
          title="Websites"
          isFixed
          howToLink="https://help.respona.com/en/articles/6332415-how-to-use-campaigns"
        >
          <HeaderActions
            testIdPrefix="publisher-websites"
            secondaryBtn={{
              title: 'Import CSV',
              testId: 'publisher-websites-import',
              icon: 'upload',
              handleClick: () => setShowImportModal(true),
              // A running import has no banner of its own (RES-7302); the button is the
              // indicator, and clicking it reopens the modal on the status view.
              isLoading: websiteImport.isInProgress,
              isLoadingText: 'Importing...',
            }}
            addNewBtn={{
              title: 'Add a new site',
              testId: 'publisher-websites-add',
              handleClick: () => createNew(),
            }}
            handleSearchValue={debouncedSetSearchString}
            searchPlaceholder="Search"
            initialFilterState={initialFilterState}
            filterFields={getPublisherWebsiteFilterFields()}
            appliedFilters={appliedFilters}
            onApplyFilters={(filters) => setAppliedFilters(filters)}
          />
        </PageHeader>
        <div>
          <WebsitesTable appliedFilters={appliedFilters} isLoading={false} />
        </div>

        <ImportWebsitesModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          websiteImport={websiteImport}
        />
      </div>
    </div>
  );
}

export default Websites;
